import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { useI18nT } from '../../i18n/index.js';

export interface SliderProps {
  value?: number;
  defaultValue?: number;
  onValueChange?: (value: number) => void;
  onValueCommit?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  showValue?: boolean;
  formatValue?: (value: number) => string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  name?: string;
  id?: string;
  className?: string;
  ref?: Ref<HTMLSpanElement>;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

function clamp(v: number, min: number, max: number): number {
  if (v < min) return min;
  if (v > max) return max;
  return v;
}

/** Safe across browsers (no `process` global) and Node (no `import.meta.env`).
 *  Only suppress the dev-warning in prod; in all other contexts (browser dev
 *  server, Node tests, jsdom) we want the helpful message. */
function isProd(): boolean {
  const maybeProcess = (globalThis as {
    process?: { env?: Record<string, string | undefined> };
  }).process;
  return maybeProcess?.env?.['NODE_ENV'] === 'production';
}

function snapToStep(v: number, min: number, step: number): number {
  if (step <= 0) return v;
  const raw = Math.round((v - min) / step) * step + min;
  // Compensate for floating-point drift from repeated addition.
  const decimals = (String(step).split('.')[1] ?? '').length;
  if (decimals > 0) {
    const factor = 10 ** decimals;
    return Math.round(raw * factor) / factor;
  }
  return raw;
}

/** Fully self-developed slider — div track + absolute-positioned thumb.
 *  No <input type="range">. Pointer Events for unified mouse/touch/pen.
 *  Keyboard (WAI-ARIA slider pattern): ←↓ decrement, →↑ increment,
 *  Home/End to min/max, PageDown/PageUp for 10× step. */
export function Slider(props: SliderProps): ReactNode {
  const t = useI18nT();
  const {
    value: controlledValue,
    defaultValue,
    onValueChange,
    onValueCommit,
    min = 0,
    max = 100,
    step = 1,
    label,
    showValue = false,
    formatValue,
    disabled = false,
    size = 'md',
    name,
    id,
    className,
    ref,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  } = props;

  const isControlled = controlledValue !== undefined;
  const initial = defaultValue ?? controlledValue ?? min;
  const [uncontrolled, setUncontrolled] = useState<number>(
    clamp(snapToStep(initial, min, step), min, max),
  );
  const raw = controlledValue !== undefined ? controlledValue : uncontrolled;
  const value = clamp(raw, min, max);

  const trackRef = useRef<HTMLSpanElement | null>(null);
  const thumbRef = useRef<HTMLSpanElement | null>(null);
  const draggingRef = useRef<boolean>(false);

  const setTrackRef = useCallback(
    (node: HTMLSpanElement | null) => {
      trackRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref !== undefined && ref !== null) {
        (ref as { current: HTMLSpanElement | null }).current = node;
      }
    },
    [ref],
  );

  const commit = useCallback(
    (next: number, committed: boolean) => {
      if (disabled) return;
      const snapped = clamp(snapToStep(next, min, step), min, max);
      if (snapped !== value) {
        if (!isControlled) setUncontrolled(snapped);
        onValueChange?.(snapped);
      }
      if (committed) onValueCommit?.(snapped);
    },
    [disabled, isControlled, min, max, step, value, onValueChange, onValueCommit],
  );

  const valueFromClientX = useCallback(
    (clientX: number): number => {
      const track = trackRef.current;
      if (track === null) return value;
      const rect = track.getBoundingClientRect();
      if (rect.width <= 0) return value;
      const frac = clamp((clientX - rect.left) / rect.width, 0, 1);
      return min + frac * (max - min);
    },
    [value, min, max],
  );

  const onPointerDown = (e: ReactPointerEvent<HTMLSpanElement>): void => {
    if (disabled) return;
    if (e.button !== 0 && e.pointerType === 'mouse') return; // only primary button
    e.preventDefault();
    draggingRef.current = true;
    const thumb = thumbRef.current;
    thumb?.focus();
    try {
      thumb?.setPointerCapture(e.pointerId);
    } catch {
      /* jsdom or unsupported — tolerate */
    }
    commit(valueFromClientX(e.clientX), false);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLSpanElement>): void => {
    if (!draggingRef.current || disabled) return;
    commit(valueFromClientX(e.clientX), false);
  };

  const endDrag = useCallback(
    (e: ReactPointerEvent<HTMLSpanElement>, committed: boolean): void => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      const thumb = thumbRef.current;
      try {
        if (thumb?.hasPointerCapture(e.pointerId) === true) {
          thumb.releasePointerCapture(e.pointerId);
        }
      } catch {
        /* tolerate */
      }
      if (committed) commit(valueFromClientX(e.clientX), true);
    },
    [commit, valueFromClientX],
  );

  const onPointerUp = (e: ReactPointerEvent<HTMLSpanElement>): void => {
    endDrag(e, true);
  };
  const onPointerCancel = (e: ReactPointerEvent<HTMLSpanElement>): void => {
    endDrag(e, false);
  };

  // Safety net: if the pointer is released outside the track/thumb we still
  // want to stop dragging. (setPointerCapture on the thumb covers most cases
  // but some browsers lose capture on tab switch.)
  useEffect(() => {
    const stop = (): void => {
      draggingRef.current = false;
    };
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => {
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, []);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLSpanElement>): void => {
    if (disabled) return;
    const big = step * 10;
    let next: number | null = null;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        next = value - step;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        next = value + step;
        break;
      case 'PageDown':
        next = value - big;
        break;
      case 'PageUp':
        next = value + big;
        break;
      case 'Home':
        next = min;
        break;
      case 'End':
        next = max;
        break;
      default:
        return;
    }
    e.preventDefault();
    commit(next, true);
  };

  const pct = max === min ? 0 : ((value - min) / (max - min)) * 100;
  const pctStr = `${pct.toFixed(2)}%`;
  const displayText = formatValue !== undefined ? formatValue(value) : String(value);

  // WCAG requires an accessible name — guarantee one so screen readers
  // never announce an anonymous "slider". Derived in this order:
  //   1. aria-labelledby (caller owns the label element)
  //   2. aria-label       (caller passed text)
  //   3. label prop       (Slider's friendly name prop)
  //   4. fallback          ("Slider") — dev warning in non-production.
  const resolvedAriaLabel =
    ariaLabelledBy !== undefined
      ? undefined
      : (props['aria-label'] ?? label ?? t('slider.defaultLabel'));
  if (
    !isProd() &&
    ariaLabelledBy === undefined &&
    props['aria-label'] === undefined &&
    label === undefined
  ) {
    console.warn(
      '[vq Slider] missing accessible name: pass `label`, `aria-label`, or `aria-labelledby`.',
    );
  }

  const rootClasses = ['vq-slider-wrap', className].filter(Boolean).join(' ');
  const stateAttr = disabled ? 'disabled' : 'idle';

  return (
    <span
      className={rootClasses}
      data-component="Slider"
      data-size={size}
      data-state={stateAttr}
      style={{ ['--vq-slider-progress' as string]: pctStr }}
    >
      {/* Track is the click/drag surface, not the interactive role owner. */}
      <span
        ref={setTrackRef}
        className="vq-slider__track"
        data-slot="track"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <span className="vq-slider__fill" data-slot="fill" style={{ width: pctStr }} />
        <span
          ref={thumbRef}
          className="vq-slider__thumb"
          data-slot="thumb"
          role="slider"
          id={id}
          tabIndex={disabled ? -1 : 0}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={formatValue !== undefined ? displayText : undefined}
          aria-label={resolvedAriaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-disabled={disabled || undefined}
          aria-orientation="horizontal"
          onKeyDown={onKeyDown}
          style={{ left: pctStr }}
        />
      </span>
      {showValue ? (
        <span className="vq-slider__value" aria-hidden="true">
          {displayText}
        </span>
      ) : null}
      {name !== undefined ? <input type="hidden" name={name} value={String(value)} /> : null}
    </span>
  );
}
